import * as THREE from 'three'
import { getAsset } from '../assets/catalog'
import type { AssetDefinition } from '../assets/types'
import { GRID_STEP } from '../render/theme'
import {
  boxesOverlap,
  footprintBox,
  rotatedFootprint,
  type PlacedItem,
  type RoomConfig,
  type RotationSteps,
  type WallSide,
} from '../state/document'

const _raycaster = new THREE.Raycaster()
const _ndc = new THREE.Vector2()
const _hit = new THREE.Vector3()
// Hessian normal form: n·x + constant = 0, so a plane at y = h needs constant = -h.
const _plane = new THREE.Plane(new THREE.Vector3(0, 1, 0), 0)

/** Converts a pointer event to a point on the horizontal plane at `planeY`. */
export function groundPoint(
  event: { clientX: number; clientY: number },
  canvas: HTMLCanvasElement,
  camera: THREE.Camera,
  planeY = 0,
): THREE.Vector3 | null {
  const rect = canvas.getBoundingClientRect()
  if (rect.width === 0 || rect.height === 0) return null
  _ndc.set(
    ((event.clientX - rect.left) / rect.width) * 2 - 1,
    -((event.clientY - rect.top) / rect.height) * 2 + 1,
  )
  _raycaster.setFromCamera(_ndc, camera)
  _plane.set(_plane.normal, -planeY)
  // Returns null for a ray parallel to the plane, and does NOT clear the target —
  // ignoring the result would freeze the ghost at its last valid position.
  return _raycaster.ray.intersectPlane(_plane, _hit) ? _hit.clone() : null
}

export function pickItem(
  event: { clientX: number; clientY: number },
  canvas: HTMLCanvasElement,
  camera: THREE.Camera,
  targets: THREE.Object3D[],
): THREE.Intersection | null {
  if (targets.length === 0) return null
  const rect = canvas.getBoundingClientRect()
  if (rect.width === 0 || rect.height === 0) return null
  _ndc.set(
    ((event.clientX - rect.left) / rect.width) * 2 - 1,
    -((event.clientY - rect.top) / rect.height) * 2 + 1,
  )
  _raycaster.setFromCamera(_ndc, camera)
  const hits = _raycaster.intersectObjects(targets, false)
  return hits[0] ?? null
}

/* ------------------------------------------------------------- snapping */

/**
 * Snaps the item's leading corner rather than its centre, so a 1.6 m desk and a
 * 0.45 m cabinet still line up edge to edge on a 0.25 m grid.
 */
export function snapPlacement(
  x: number,
  z: number,
  footprint: readonly [number, number],
  rotation: RotationSteps,
  step = GRID_STEP,
): [number, number] {
  const [w, d] = rotatedFootprint(footprint, rotation)
  const minX = Math.round((x - w / 2) / step) * step
  const minZ = Math.round((z - d / 2) / step) * step
  return [minX + w / 2, minZ + d / 2]
}

/** Items this thin never block anything (rugs, mats). */
const FLAT_HEIGHT = 0.06

export interface DropResolution {
  x: number
  z: number
  y: number
  rotation: RotationSteps
  wall?: WallSide
  valid: boolean
  /** Item this one is resting on, when stacked. */
  supportUid?: string
}

export interface ResolveDropInput {
  asset: AssetDefinition
  x: number
  z: number
  rotation: RotationSteps
  room: RoomConfig
  items: PlacedItem[]
  /** The item being moved, so it does not collide with itself. */
  excludeUid?: string
  /** Keeps a wall item's current height instead of the asset default. */
  mountHeight?: number
  snap?: boolean
}

function nearestWall(x: number, z: number, room: RoomConfig): WallSide {
  const distances: [WallSide, number][] = [
    ['north', Math.abs(z + room.depth / 2)],
    ['south', Math.abs(z - room.depth / 2)],
    ['west', Math.abs(x + room.width / 2)],
    ['east', Math.abs(x - room.width / 2)],
  ]
  distances.sort((a, b) => a[1] - b[1])
  return distances[0]?.[0] ?? 'north'
}

/**
 * Resolves a raw ground position into a legal placement: snapped, clamped to the
 * room, elevated onto whatever surface it lands on, and validated.
 */
export function resolveDrop(input: ResolveDropInput): DropResolution {
  const { asset, room, items, excludeUid, rotation } = input
  const snap = input.snap ?? true

  if (asset.anchor === 'wall') {
    const wall = nearestWall(input.x, input.z, room)
    const horizontal = wall === 'north' || wall === 'south'
    const span = horizontal ? room.width : room.depth
    const halfSpan = Math.max(0, span / 2 - asset.footprint[0] / 2)
    const raw = horizontal ? input.x : input.z
    const stepped = snap ? Math.round(raw / GRID_STEP) * GRID_STEP : raw
    const along = Math.max(-halfSpan, Math.min(halfSpan, stepped))
    return {
      x: horizontal ? along : 0,
      z: horizontal ? 0 : along,
      y: input.mountHeight ?? asset.mountHeight ?? 1.5,
      rotation,
      wall,
      valid: true,
    }
  }

  const [rawX, rawZ] = snap
    ? snapPlacement(input.x, input.z, asset.footprint, rotation)
    : [input.x, input.z]

  const [w, d] = rotatedFootprint(asset.footprint, rotation)
  const limitX = Math.max(0, room.width / 2 - w / 2)
  const limitZ = Math.max(0, room.depth / 2 - d / 2)
  const x = Math.max(-limitX, Math.min(limitX, rawX))
  const z = Math.max(-limitZ, Math.min(limitZ, rawZ))

  const box = footprintBox(x, z, asset.footprint, rotation)

  // Stacking: the tallest surface whose footprint contains our centre wins.
  let y = 0
  let supportUid: string | undefined
  if (asset.stackable) {
    for (const other of items) {
      if (other.uid === excludeUid) continue
      const otherAsset = getAsset(other.assetId)
      if (!otherAsset?.surfaceHeight || otherAsset.anchor === 'wall') continue
      const otherBox = footprintBox(other.x, other.z, otherAsset.footprint, other.rotation)
      const inside =
        x > otherBox.minX && x < otherBox.maxX && z > otherBox.minZ && z < otherBox.maxZ
      if (!inside) continue
      const top = other.y + otherAsset.surfaceHeight
      if (top > y) {
        y = top
        supportUid = other.uid
      }
    }
  }

  // Collision only applies between things actually standing on the floor.
  let valid = true
  const blocking = asset.height > FLAT_HEIGHT && !asset.stackable
  if (blocking && y === 0) {
    for (const other of items) {
      if (other.uid === excludeUid) continue
      const otherAsset = getAsset(other.assetId)
      if (!otherAsset || otherAsset.anchor === 'wall') continue
      if (otherAsset.height <= FLAT_HEIGHT || otherAsset.stackable) continue
      if (other.y > 0) continue
      if (boxesOverlap(box, footprintBox(other.x, other.z, otherAsset.footprint, other.rotation))) {
        valid = false
        break
      }
    }
  }

  return { x, z, y, rotation, valid, supportUid }
}
